import { useMemo, useState, type ReactNode } from "react";
import { useScrollToTopOnChange } from "@/hooks/useScrollToTopOnChange";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOwnerPropertyAccess } from "@/hooks/v2/useOwnerPropertyAccess";
import {
  useOwnerPropertyDetail,
  type OwnerTenant,
} from "@/hooks/owner/useOwnerPropertyDetail";
import { isPaidCurrentPeriod } from "@/lib/rent-reminder-templates";
import { useOwnerRentLedger } from "@/hooks/owner/useOwnerRentLedger";
import { useOwnerPropertyApartments } from "@/hooks/v2/useApartmentDocuments";
import { OwnerTenantDetailDialog } from "@/components/owner/OwnerTenantDetailDialog";
import { OwnerPropertyActivityTab } from "@/components/owner/OwnerPropertyActivityTab";
import { useToast } from "@/hooks/use-toast";
import { V2AdminNavbar } from "@/components/admin/v2/V2AdminNavbar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ownerTabsListClass,
  ownerTabsTriggerClass,
} from "@/components/owner/owner-v3-chrome";
import { useOwnerPortalV3 as ownerV3 } from "@/hooks/owner/useOwnerPortalV3";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronRight, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatAddress } from "@/lib/v2-format";
import { PAGE_TITLE_CLASS } from "@/components/admin/v2/page-title";

/** Detail RPC money columns are NUMERIC euros — format directly (no /100).
 *  Locale-aware like the owner tenant dialog's formatter, so an English viewer
 *  reads "€690" rather than the German "690 €". */
function formatEuro(amount: number | null | undefined, locale: string) {
  if (amount == null) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(value: string | null | undefined, locale: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** The subset of `owner_property_dashboard_metrics`'s row this page renders —
 *  the RPC also returns windowed collected totals and other fields this page
 *  doesn't show; leave them off rather than typing dead data. */
type MetricsPayload = {
  total_rooms: number;
  occupied_rooms: number;
  occupancy_rate: number;
};

/**
 * Rent status for the overview, as two states plus a suppression case.
 *
 * Derived through isPaidCurrentPeriod — the same predicate every other overdue
 * surface uses — rather than the stored, stale bookings.payment_status. The
 * dunning STAGE (overdue_1…inkasso) is deliberately not surfaced here: this
 * column answers "has this month's rent arrived", and the owner's Reminders
 * screen is where the escalation ladder belongs.
 *
 * Returns null for a unit whose reminders are switched off — those are
 * owner-collected, so CoLiviq has no basis to call the rent paid or pending
 * and the caller renders a neutral chip instead of guessing.
 */
type RentStatus = "paid" | "pending" | null;

function deriveRentStatus(ten: OwnerTenant): RentStatus {
  if (ten.reminders_enabled === false) return null;
  return isPaidCurrentPeriod(ten) ? "paid" : "pending";
}

function RentStatusChip({
  status,
  t,
}: {
  status: RentStatus;
  t: (key: string, fallback: string) => string;
}) {
  if (status === null) {
    return (
      <Badge
        variant="outline"
        className="rounded-full font-medium bg-slate-100 text-slate-500 border-slate-200"
      >
        {t("owner.detail.remindersOff", "Reminders off")}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full font-medium",
        status === "paid"
          ? "bg-emerald-100 text-emerald-700 border-emerald-200"
          : "bg-amber-100 text-amber-700 border-amber-200"
      )}
    >
      {status === "paid"
        ? t("owner.detail.rentPaid", "Paid")
        : t("owner.detail.rentPending", "Pending")}
    </Badge>
  );
}

export function OwnerPropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const propertyId = id ? Number(id) : null;
  /** Where this page was opened from. The "My properties" grid passes
   *  ?from=properties so the breadcrumb returns there; every other entry
   *  point (the analytics dashboard) keeps the Analytics crumb. */
  const [searchParams] = useSearchParams();
  const fromProperties = searchParams.get("from") === "properties";
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const { isOwnerPortalOnly } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  useScrollToTopOnChange(tab);
  const [viewingTenant, setViewingTenant] = useState<OwnerTenant | null>(null);

  const access = useOwnerPropertyAccess(isOwnerPortalOnly ? propertyId : null);
  const { data, isLoading, isError, error } =
    useOwnerPropertyDetail(propertyId);
  /** Apartment links. Without them the apartment page is unreachable. */
  const ownerApartments = useOwnerPropertyApartments(propertyId);

  const metrics = useQuery({
    queryKey: ["owner-property-metrics", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<MetricsPayload | null> => {
      const { data: raw, error: rpcError } = await supabase.rpc(
        "owner_property_dashboard_metrics",
        { _property_id: propertyId }
      );
      if (rpcError) throw rpcError;
      return raw as MetricsPayload | null;
    },
  });

  const property = data?.property;
  const rooms = data?.rooms ?? [];
  const tenants = data?.tenants ?? [];
  const history = data?.history ?? [];
  const m = metrics.data;

  /** Room id → its current tenant. Keyed, not title-matched: "Room 1" repeats
   *  across a building's apartments, and matching on it is what made a room
   *  click open someone else's tenant panel. */
  const tenantByRoomId = useMemo(() => {
    const map = new Map<number, OwnerTenant>();
    for (const ten of data?.tenants ?? []) {
      if (ten.room_id != null) map.set(Number(ten.room_id), ten);
    }
    return map;
  }, [data]);

  const occupancyPct = m?.occupancy_rate ?? 0;

  /**
   * Monthly rent and Outstanding are this month's DUE rent and what is still
   * open of it, read off the owner rent ledger -- the same one the analytics
   * page sums -- so the two pages cannot disagree. "Open" is binary: a row is
   * open until it is effectively paid (src/lib/rent.ts), whatever the tenant's
   * rent-status chip below says about reminders.
   */
  const rentLedger = useOwnerRentLedger(propertyId);
  const monthlyRentCents = rentLedger.data?.dueCents ?? 0;
  const outstandingCents = rentLedger.data?.openCents ?? 0;

  // Owner-only user who explicitly does not own this property → bounce.
  if (isOwnerPortalOnly && access.data === false) {
    return <Navigate to="/admin?tab=analytics" replace />;
  }

  const notFound = !isLoading && !isError && data === null;

  return (
    <div className="min-h-screen bg-background">
      <V2AdminNavbar />
      <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 lg:px-8 pt-6 pb-12 space-y-6">
        {/* Breadcrumb rather than a bare back-link, per the design — it says
            where you are, not just where you came from. */}
        <nav
          aria-label={t("owner.breadcrumbAriaLabel", "Breadcrumb")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to={fromProperties ? "/admin/properties" : "/admin?tab=analytics"}
            className="hover:text-foreground transition-colors"
          >
            {fromProperties
              ? t("admin.nav.myProperties", "My properties")
              : t("admin.nav.ownerAnalytics", "Analytics")}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground font-medium truncate">
            {property?.title ?? t("owner.detail.property", "Property")}
          </span>
        </nav>

        {isError ? (
          <Alert variant="destructive" className="rounded-xl">
            <AlertTitle>
              {t("owner.detail.loadErrorTitle", "Could not load property")}
            </AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : t(
                    "owner.detail.loadErrorHint",
                    "Apply the latest Supabase migrations so the owner_property_detail function exists, then retry."
                  )}
            </AlertDescription>
          </Alert>
        ) : null}

        {notFound ? (
          <Alert className="rounded-xl">
            <AlertTitle>
              {t("owner.detail.notFoundTitle", "Property unavailable")}
            </AlertTitle>
            <AlertDescription>
              {t(
                "owner.detail.notFoundHint",
                "This property may have been removed, or it is not assigned to your account."
              )}
            </AlertDescription>
          </Alert>
        ) : null}

        {/* Header */}
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {isLoading ? (
              <Skeleton className="h-8 w-72 rounded-lg" />
            ) : (
              <h1 className={`flex items-center gap-2.5 ${PAGE_TITLE_CLASS}`}>
                {property?.title ?? t("owner.detail.property", "Property")}
                {property?.city ? (
                  <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                    {property.city}
                  </span>
                ) : null}
              </h1>
            )}
          </div>
          {property?.address || property?.city ? (
            <p className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              {formatAddress(
                property?.address,
                property?.zip_code,
                property?.city,
                " · "
              )}
            </p>
          ) : null}
        </div>

        {(ownerApartments.data?.length ?? 0) > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("owner.detail.apartments", "Apartments")}
            </span>
            {ownerApartments.data?.map((a) => (
              <Link
                key={a.id}
                to={`/admin/owner/apartment/${a.id}`}
                className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-medium text-teal-700 transition-colors hover:bg-muted"
              >
                {a.label}
              </Link>
            ))}
          </div>
        ) : null}

        {/* KPI strip — label + figure only, per the owner-portal design: the
            four read as one row of numbers, with the occupancy bar the single
            piece of ornament. */}
        <div
          className={
            ownerV3()
              ? "grid gap-x-8 gap-y-7 border-b border-[#ECE7DD] pb-7 sm:grid-cols-2 xl:grid-cols-4"
              : "grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
          }
        >
          <KpiCard
            title={t("owner.detail.kpiOccupiedRooms", "Occupied rooms")}
            value={
              metrics.isLoading
                ? "—"
                : `${m?.occupied_rooms ?? 0}/${m?.total_rooms ?? 0}`
            }
          />
          <KpiCard
            title={t("owner.detail.kpiOccupancy", "Occupancy")}
            value={metrics.isLoading ? "—" : `${Math.round(occupancyPct)}%`}
          >
            <div
              className={cn(
                "mt-3 h-2 w-full overflow-hidden rounded-full",
                ownerV3() ? "bg-[#ECE7DD]" : "bg-muted"
              )}
            >
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  ownerV3() ? "bg-[#2FB3AA]" : "bg-teal-400"
                )}
                style={{ width: `${Math.min(100, occupancyPct)}%` }}
              />
            </div>
          </KpiCard>
          <KpiCard
            title={t("owner.detail.kpiMonthlyRent", "Monthly rent")}
            value={
              rentLedger.isLoading
                ? "—"
                : formatEuro(monthlyRentCents / 100, locale)
            }
          />
          <KpiCard
            title={t("owner.detail.kpiOutstanding", "Outstanding")}
            value={
              rentLedger.isLoading
                ? "—"
                : formatEuro(outstandingCents / 100, locale)
            }
            titleClass={ownerV3() ? undefined : "text-amber-700/90"}
            valueClass={
              ownerV3()
                ? outstandingCents > 0
                  ? "text-[#C0714F]"
                  : undefined
                : "text-amber-600"
            }
          />
        </div>

        {/* Two tabs, Overview first. There is no Rooms tab and no Tenants
            tab: both are the Overview's own columns, each linking to its own
            view, and a second table of either could only disagree with the
            first. There is no History tab either: past tenancies belong to a
            room, so they live on the room page, not on a building or unit.
            There is no Documents tab either (COL-628): every file lives on
            the sidebar's Dokumente page, which drills down building → unit
            → room → tenant, so a per-building copy here could only drift. */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className={ownerTabsListClass(ownerV3())}>
            <TabsTrigger
              value="overview"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.detail.tabOverview", "Overview")}
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              className={ownerTabsTriggerClass(ownerV3())}
            >
              {t("owner.detail.tabActivity", "Activity")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* The page's own overview: the rent roll room by room. */}
            <PanelCard title={t("owner.detail.roomOverview", "Overview")}>
              {isLoading ? (
                <TableSkeleton cols={4} />
              ) : rooms.length === 0 ? (
                <EmptyRow
                  text={t(
                    "owner.detail.noRooms",
                    "No rooms in this property yet."
                  )}
                />
              ) : (
                <ScrollTable>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className={COL_HEAD}>
                        {t("owner.detail.colRoom", "Room")}
                      </TableHead>
                      <TableHead className={COL_HEAD}>
                        {t("owner.detail.colTenant", "Tenant")}
                      </TableHead>
                      <TableHead className={COL_HEAD}>
                        {t("owner.detail.colMovein", "Move-in")}
                      </TableHead>
                      <TableHead className={cn(COL_HEAD, "text-right")}>
                        {t("owner.detail.colRentStatus", "Rent status")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Row = room. The room name and the tenant name are two
                        separate links to two separate views; the row itself is
                        not clickable, which is what used to send a room click
                        into the tenant panel. */}
                    {rooms.map((r) => {
                      const ten = tenantByRoomId.get(Number(r.id)) ?? null;
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">
                            <Link
                              to={`/admin/owner/room/${r.id}`}
                              className="text-teal-700 hover:underline"
                            >
                              {r.title ?? t("owner.detail.room", "Room")}
                            </Link>
                            {/* Only when the property actually carries these — an
                              empty floor/size leaves the row exactly as designed. */}
                            {r.floor || r.size_sqm != null ? (
                              <span className="block text-xs font-normal text-muted-foreground">
                                {[
                                  r.floor,
                                  r.size_sqm != null
                                    ? `${r.size_sqm} m²`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {ten ? (
                              <button
                                type="button"
                                onClick={() => setViewingTenant(ten)}
                                className="text-left font-medium text-teal-700 hover:underline"
                              >
                                {ten.name ?? t("owner.detail.tenant", "Tenant")}
                              </button>
                            ) : (
                              <OccupancyPill occupied={false} t={t} />
                            )}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {ten ? formatDate(ten.move_in_date, locale) : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {ten ? (
                              <RentStatusChip
                                status={deriveRentStatus(ten)}
                                t={t}
                              />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </ScrollTable>
              )}
            </PanelCard>
          </TabsContent>

          {/* Activity — everything dated that has happened to this property,
              newest first. Its own component because the feed is assembled from
              five sources and paginated; inlining it here would bury the page's
              own structure. */}
          <TabsContent value="activity">
            <OwnerPropertyActivityTab
              propertyId={propertyId}
              active={tab === "activity"}
              tenants={tenants}
              history={history}
              apartments={ownerApartments.data ?? []}
              rooms={rooms}
              locale={locale}
            />
          </TabsContent>
        </Tabs>
      </div>

      <OwnerTenantDetailDialog
        tenant={viewingTenant}
        propertyId={propertyId}
        propertyTitle={property?.title ?? ""}
        open={!!viewingTenant}
        onOpenChange={(o) => !o && setViewingTenant(null)}
      />
    </div>
  );
}

function KpiCard({
  title,
  value,
  hint,
  children,
  titleClass,
  valueClass,
}: {
  title: string;
  value: string;
  hint?: string;
  children?: ReactNode;
  titleClass?: string;
  valueClass?: string;
}) {
  // V3 prints the figures straight onto the page, as the prototype does and as
  // the Analytics tiles now do. Four bordered boxes in a row are four things to
  // look past to compare four numbers.
  if (ownerV3()) {
    return (
      <div>
        <p
          className={cn(
            "text-[13px] leading-tight",
            titleClass ?? "text-[#7A8E8C]"
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            "mt-1.5 text-[26px] font-bold leading-none tracking-tight tabular-nums",
            valueClass ?? "text-[#0F4C4A]"
          )}
        >
          {value}
        </p>
        {children}
        {hint ? <p className="mt-1.5 text-xs text-[#7A8E8C]">{hint}</p> : null}
      </div>
    );
  }

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
      <CardContent className="p-5">
        {/* Label + number only, no icon tile: the strip reads as one row of
            figures. */}
        <p
          className={cn(
            "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight",
            titleClass
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            "mt-2 text-[26px] font-bold leading-none tabular-nums tracking-tight",
            valueClass
          )}
        >
          {value}
        </p>
        {children}
        {hint ? (
          <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Overview panel: a plain white card with the title sitting inside it, no
 * tinted header bar — the two panels under the KPI strip are meant to read as
 * one surface each, not as banner-plus-body like the tabbed sections below.
 */
function PanelCard({
  title,
  action,
  className,
  children,
}: {
  title: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card
      className={cn(
        "rounded-2xl border-border/60 shadow-sm overflow-hidden",
        className
      )}
    >
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {action}
      </div>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

/** Column headers in the overview table: small, uppercase, quiet. */
const COL_HEAD =
  "h-9 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80";

function OccupancyPill({
  occupied,
  t,
}: {
  occupied: boolean;
  t: (key: string, fallback?: string) => string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        occupied
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          occupied ? "bg-emerald-500" : "bg-slate-400"
        )}
        aria-hidden
      />
      {occupied
        ? t("owner.detail.occupied", "Occupied")
        : t("owner.detail.vacant", "Vacant")}
    </span>
  );
}

function ScrollTable({ children }: { children: ReactNode }) {
  return (
    <div className="w-full overflow-x-auto">
      <Table>{children}</Table>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <p className="text-sm text-muted-foreground text-center py-12">{text}</p>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} className="h-6 flex-1 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default OwnerPropertyDetail;
